import { create } from 'zustand';
import {
  ApiError,
  OfflineError,
  onRefreshTokenRotated,
  redeemEnrolment,
  setRefreshToken,
} from '../lib/api';
import { metaGet, metaSet, wipeDatabase } from '../lib/db';
import {
  forgetToken,
  hasVault,
  isCryptoAvailable,
  MAX_PIN_ATTEMPTS,
  storeToken,
  unlock,
} from '../lib/pin-vault';

export interface DriverIdentity {
  id: string;
  fullName: string;
  phone: string | null;
}

type Phase = 'loading' | 'enrol' | 'locked' | 'ready' | 'unsupported';

interface SessionState {
  phase: Phase;
  driver: DriverIdentity | null;
  attemptsLeft: number;
  busy: boolean;
  error: string | null;
  init: () => Promise<void>;
  enrol: (code: string, pin: string) => Promise<boolean>;
  unlockWithPin: (pin: string) => Promise<boolean>;
  lock: () => void;
  signOut: () => Promise<void>;
  clearError: () => void;
}

/**
 * ПИН-ът се пази в паметта само докато приложението е отключено — трябва ни,
 * ако сървърът завърти нов токен за подновяване и трябва да го запишем отново
 * шифрован. Не отива нито на диска, нито по мрежата.
 */
let sessionPin: string | null = null;

onRefreshTokenRotated(async (token) => {
  if (sessionPin) await storeToken(sessionPin, token);
});

export const useSession = create<SessionState>((set) => ({
  phase: 'loading',
  driver: null,
  attemptsLeft: MAX_PIN_ATTEMPTS,
  busy: false,
  error: null,

  init: async () => {
    if (!isCryptoAvailable()) {
      set({ phase: 'unsupported' });
      return;
    }
    const driver = (await metaGet<DriverIdentity>('auth.driver')) ?? null;
    set({ driver, phase: (await hasVault()) ? 'locked' : 'enrol' });
  },

  enrol: async (code, pin) => {
    set({ busy: true, error: null });
    try {
      const result = await redeemEnrolment(code);
      await storeToken(pin, result.refreshToken);
      await metaSet('auth.driver', result.driver);
      sessionPin = pin;
      set({ phase: 'ready', driver: result.driver, busy: false, attemptsLeft: MAX_PIN_ATTEMPTS });
      return true;
    } catch (error) {
      const message =
        error instanceof OfflineError
          ? 'Няма връзка. Свързването на телефона става веднъж, в офиса, с интернет.'
          : error instanceof ApiError
            ? error.message
            : 'Нещо се обърка. Опитайте отново.';
      set({ busy: false, error: message });
      return false;
    }
  },

  unlockWithPin: async (pin) => {
    set({ busy: true, error: null });
    const result = await unlock(pin);
    if (result.ok && result.refreshToken) {
      sessionPin = pin;
      setRefreshToken(result.refreshToken);
      set({ phase: 'ready', busy: false, attemptsLeft: MAX_PIN_ATTEMPTS });
      return true;
    }
    if (result.wiped) {
      await wipeDatabase();
      set({
        phase: 'enrol',
        busy: false,
        driver: null,
        attemptsLeft: MAX_PIN_ATTEMPTS,
        error: 'Десет сгрешени ПИН-а. Обадете се на диспечера за нов код за свързване.',
      });
      return false;
    }
    set({
      busy: false,
      attemptsLeft: result.attemptsLeft,
      error: `Грешен ПИН. Остават ${result.attemptsLeft} опита.`,
    });
    return false;
  },

  lock: () => {
    sessionPin = null;
    setRefreshToken(null);
    set({ phase: 'locked', error: null });
  },

  signOut: async () => {
    sessionPin = null;
    setRefreshToken(null);
    await forgetToken();
    await wipeDatabase();
    set({ phase: 'enrol', driver: null, error: null, attemptsLeft: MAX_PIN_ATTEMPTS });
  },

  clearError: () => set({ error: null }),
}));

/** Използва се от синхронизацията, за да не тръгва, докато телефонът е заключен. */
export function isUnlocked(): boolean {
  return useSession.getState().phase === 'ready';
}
